import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Package } from "lucide-react";
import "./NewProductCard.css";

// ====================================================================
// HELPER FUNCTIONS (Outside component to prevent recreation)
// ====================================================================
const capitalizeWords = (str) => {
  if (!str) return '';
  return str
    .toLowerCase()
    .replace(/\b\w/g, char => char.toUpperCase());
};

const getDisplayTags = (tags) => {
  if (!tags || tags.length === 0) return [];
  
  return tags
    .filter(tag => 
      tag.slug !== "limited-stock" && 
      tag.slug !== "best-selling" && 
      tag.name.toLowerCase() !== "best selling"
    )
    .sort((a, b) => {
      const priorityOrder = { 'new': 1, 'for-sale': 2, 'limited-stock': 3 };
      const priorityA = priorityOrder[a.slug] || 999;
      const priorityB = priorityOrder[b.slug] || 999;
      return priorityA - priorityB;
    })
    .slice(0, 3);
};

const getLimitedStockTags = (tags) => {
  if (!tags || tags.length === 0) return [];
  return tags.filter(tag => tag.slug === "limited-stock");
};

// ====================================================================
// MAIN COMPONENT
// ====================================================================
const NewProductCard = ({ product, taxonomy }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  // Memoized values
  const brandLogo = useMemo(() => {
    return product.acf_fields?.brand_logo?.url || null;
  }, [product.acf_fields?.brand_logo?.url]);

  const visibleSpecs = useMemo(() => {
    const specs = [];
    if (product.acf_fields?.weight?.trim()) {
      specs.push({ 
        type: 'weight', 
        value: capitalizeWords(product.acf_fields.weight),
        icon: '/images/icons/weight.png'
      });
    }
    if (product.acf_fields?.dimension?.trim()) {
      specs.push({ 
        type: 'dimension', 
        value: product.acf_fields.dimension,
        icon: '/images/icons/dimension.png'
      });
    }
    if (product.acf_fields?.engine?.trim()) {
      specs.push({ 
        type: 'engine', 
        value: capitalizeWords(product.acf_fields.engine),
        icon: '/images/icons/engine.png'
      });
    }
    return specs;
  }, [product.acf_fields]);

  const displayTags = useMemo(() => getDisplayTags(product.tags), [product.tags]);
  const limitedStockTags = useMemo(() => getLimitedStockTags(product.tags), [product.tags]);

  // Event handlers
  const handleImageError = () => {
    setImageError(true);
    setImageLoading(false);
  };

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const productUrl = `/product/${product.slug}/gs${product.id}`;

  return (
    <div className="new-product-card">
      {/* Image Section - Clickable */}
      <Link 
        to={productUrl}
        className="new-product-card__image-link"
        aria-label={`View ${product.name}`}
      >
        <div className="new-product-card__image-wrapper">
          {!imageError && product.images && product.images[0] ? (
            <>
              {imageLoading && (
                <div 
                  className="new-product-card__loading" 
                  role="status" 
                  aria-label="Loading product image"
                >
                  <div className="new-product-card__spinner" />
                </div>
              )}
              <img
                src={product.images[0]}
                alt={product.name || "Product image"}
                className={`new-product-card__image ${
                  imageLoading ? 'new-product-card__image--loading' : 'new-product-card__image--loaded'
                }`}
                onError={handleImageError}
                onLoad={handleImageLoad}
                loading="lazy"
              />
            </>
          ) : (
            <div 
              className="new-product-card__image-placeholder"
              role="img"
              aria-label="No product image available"
            >
              <Package className="new-product-card__placeholder-icon" size={48} />
            </div>
          )}
        </div>
      </Link>

      {/* Content Section */}
      <div className="new-product-card__content">
        {/* Tags */}
        {(displayTags.length > 0 || limitedStockTags.length > 0) && (
          <div className="new-product-card__tags">
            <div className="new-product-card__tags-left">
              {displayTags.map((tag, index) => (
                <span key={`${tag.slug}-${index}`} className={`new-product-card__tag new-product-card__tag--${tag.slug}`}>
                  {tag.name}
                </span>
              ))}
            </div>
            <div className="new-product-card__tags-right">
              {limitedStockTags.map((tag, index) => (
                <span key={`limited-${index}`} className="new-product-card__tag new-product-card__tag--limited-stock">
                  {tag.name}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Header with Title and Logo - Title is Clickable */}
        <div className="new-product-card__header">
          <div className="new-product-card__title">
            <Link 
              to={productUrl}
              className="new-product-card__title-link"
            >
              <h2>{product.name}</h2>
            </Link>
          </div>
          {brandLogo && (
            <div className="new-product-card__logo">
              <img 
                src={brandLogo} 
                alt={`${product.acf_fields?.brand_name || 'Brand'} logo`}
                loading="lazy"
              />
            </div>
          )}
        </div>

        {/* Specifications */}
        {visibleSpecs.length > 0 && (
          <div className="new-product-card__specifications">
            {visibleSpecs.map((spec, index) => (
              <div key={spec.type} className="new-product-card__spec-item">
                <span className="new-product-card__spec-icon">
                  <img src={spec.icon} alt="" />
                </span>
                <span className="new-product-card__spec-value">{spec.value}</span>
              </div>
            ))}
          </div>
        )}

        {/* Get a Quote Button */}
        <div className="new-product-card__actions">
          <Link 
            to={`/product/${product.slug}/gs${product.id}`}
            className="new-product-card__quote-btn"
            aria-label={`Get a quote for ${product.name}`}
          >
            <span>Get a Quote</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

// ====================================================================
// SKELETON LOADER
// ====================================================================
export const ProductCardSkeleton = () => {
  return (
    <div className="new-product-card new-product-card--skeleton">
      <div className="new-product-card__image-wrapper new-product-card__image-wrapper--skeleton">
        <div className="skeleton-box skeleton-box--image"></div>
      </div>

      <div className="new-product-card__content">
        <div className="new-product-card__header">
          <div className="new-product-card__title">
            <div className="skeleton-box skeleton-box--title"></div>
            <div className="skeleton-box skeleton-box--meta"></div>
          </div>
          <div className="skeleton-box skeleton-box--logo"></div>
        </div>

        <div className="new-product-card__actions">
          <div className="skeleton-box skeleton-box--btn"></div>
        </div>
      </div>
    </div>
  );
};

// ====================================================================
// MEMOIZATION
// ====================================================================
export default React.memo(NewProductCard, (prevProps, nextProps) => {
  // Deep comparison for better memoization
  return (
    prevProps.product.id === nextProps.product.id &&
    prevProps.product.name === nextProps.product.name &&
    prevProps.product.slug === nextProps.product.slug &&
    prevProps.product.images?.[0] === nextProps.product.images?.[0] &&
    JSON.stringify(prevProps.product.tags) === JSON.stringify(nextProps.product.tags) &&
    prevProps.product.acf_fields?.weight === nextProps.product.acf_fields?.weight &&
    prevProps.product.acf_fields?.dimension === nextProps.product.acf_fields?.dimension &&
    prevProps.product.acf_fields?.engine === nextProps.product.acf_fields?.engine &&
    prevProps.product.acf_fields?.brand_logo?.url === nextProps.product.acf_fields?.brand_logo?.url
  );
});